import { css } from '@emotion/react';
import { MEDIA_QUERIES, ThemeType } from 'radiance-ui/lib/constants';

/**
 * Figma specifies a warm charcoal scrim for the Agency Treatment modal. The
 * rebrand theme only offers `COLORS.overlaySolid` (#1D1D44), so the design
 * value lives here until radiance exposes a matching token.
 */
const SCRIM = '#302e37';

/**
 * `.modal-offclick-wrapper` is radiance-ui's own class on `ImmersiveModal`, and
 * the component exposes no props for scrim, width, or radius. These rules are
 * therefore coupled to radiance's markup and must be revalidated on every
 * radiance upgrade — `immersiveModals/index.test.tsx` and the Treatment E2E
 * spec are the regression guards. Selectors intentionally match structure
 * rather than user-facing copy so a label change cannot break them.
 */
const agencyTreatmentModal = (theme: ThemeType) => css`
  background: ${SCRIM};

  > div {
    box-sizing: border-box;
    padding: 44px 0 0;
  }

  // Figma 2729:62664 bleeds the sheet past both edges of the 375px frame (its
  // rectangle is 383 wide) and insets only the content, so the sheet takes the
  // full width here and the body below owns the 20px content margin.
  .modal-offclick-wrapper {
    height: calc(100% - 44px);
    margin: 0;
    max-width: none;
  }

  .modal-offclick-wrapper > div:first-of-type {
    display: none;
  }

  .modal-offclick-wrapper > div:last-of-type {
    background: ${theme.COLORS.background};
    border-radius: 8px 8px 0 0;
    box-shadow: none;
    display: flex;
    flex-direction: column;
    height: 100%;
  }

  // radiance centres a 16x16 cross inside a fixed 40x40 button, so the ink sits
  // 12px inside every edge. Figma 2729:62727 puts that cross at (339, 66) in the
  // 375px frame — 22px below the sheet top, 20px in from the right — which is
  // this offset plus that inset. Figma hides the 40x40 circle behind it but
  // keeps it as the tap target, so only the white fill goes.
  //
  // Above md radiance switches the button to fixed positioning with a 4.5rem top,
  // pinning it to the viewport instead of the dialog. The Agency dialog sits 88px
  // down, so the cross landed 4px ABOVE the card and straddled its top border —
  // the close button QA reported. Anchoring it to the card, which radiance already
  // makes a positioned ancestor, keeps one set of offsets at every breakpoint.
  .modal-offclick-wrapper > div:last-of-type > button {
    background: transparent;
    right: 8px;
    top: 10px;
  }

  // Figma marks the modal content as scrollable. Without flexing and allowing
  // this child to shrink, a tall prescription list is clipped by the sheet
  // instead of scrolling inside it.
  .modal-offclick-wrapper > div:last-of-type > div:last-of-type {
    background: ${theme.COLORS.background};
    border-radius: 8px 8px 0 0;
    flex: 1 1 auto;
    min-height: 0;
    overflow-y: auto;
    // Figma puts the title 72px below the sheet top (2729:62671 sits at y=116 in
    // the frame, sheet top 44) so it clears the close button instead of sharing
    // its row, and insets the 335px content column to x=20..355.
    padding: 72px 20px 32px;
  }

  .modal-offclick-wrapper section {
    color: ${theme.COLORS.primary};
  }

  .modal-offclick-wrapper section + * {
    margin: 0;
  }

  .modal-offclick-wrapper h1,
  .modal-offclick-wrapper h2,
  .modal-offclick-wrapper [class*='ModalTitle'] {
    color: ${theme.COLORS.secondary};
    font-family: ${theme.FONTS.headerFont};
    font-size: 24px;
    font-weight: ${theme.TYPOGRAPHY.fontWeight.normal};
    line-height: 32px;
    // Figma 2729:62671 ends at y=148 and the ingredient card starts at 158.
    margin-bottom: 10px;
  }

  .modal-offclick-wrapper sup {
    font-family: ${theme.FONTS.baseFont};
    font-size: 15.48px;
  }

  // Figma only specifies the mobile sheet, so above md every piece of that
  // geometry is handed back to radiance and only the rebrand palette and
  // typography above survive. Leaving the 335px cap in place rendered the
  // desktop dialog as a phone-width sheet with a scrollbar down the middle.
  ${MEDIA_QUERIES.mdUp} {
    > div {
      padding: 0;
    }

    .modal-offclick-wrapper {
      height: auto;
      max-width: none;
    }

    .modal-offclick-wrapper > div:first-of-type {
      display: revert;
    }

    .modal-offclick-wrapper > div:last-of-type {
      display: revert;
      height: auto;
    }

    .modal-offclick-wrapper > div:last-of-type,
    .modal-offclick-wrapper > div:last-of-type > div:last-of-type {
      border-radius: 8px;
    }

    // Only the containing block changes above md; the offsets above still apply.
    .modal-offclick-wrapper > div:last-of-type > button {
      position: absolute;
    }

    .modal-offclick-wrapper > div:last-of-type > div:last-of-type {
      flex: revert;
      min-height: revert;
      overflow-y: revert;
      padding: 24px 16px 32px;
    }

    .modal-offclick-wrapper h1,
    .modal-offclick-wrapper h2,
    .modal-offclick-wrapper [class*='ModalTitle'] {
      margin-bottom: 24px;
    }
  }
`;

export default { agencyTreatmentModal };
